import { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'

import { Card } from 'pages/Toolbox/StandardParts/components/ui/card'
import { Button } from 'pages/Toolbox/StandardParts/components/ui/button'
import { ArrowLeft } from 'lucide-react'

import ContentBodyWrapper from 'components/custom/ContentBodyWrapper'
import { SustamizeSpinner } from 'pages/Toolbox/Interpreter/components/sustamizeSpinner/sustamizeSpinner'

import { CountrySelector } from 'pages/Toolbox/StandardParts/components/entityDetail/CountrySelector'
import { EmissionSummary } from 'pages/Toolbox/StandardParts/components/entityDetail/EmissionSummary'
import { EmissionCharts } from 'pages/Toolbox/StandardParts/components/entityDetail/EmissionCharts'
import { ProcessChain } from 'pages/Toolbox/StandardParts/components/entityDetail/ProcessChain'
import { EmissionHeader } from './components/EmissionHeader'

import {
  sagaGetPreMadeAssemblyAction,
  sagaGetSearchResultsAction,
} from 'saga/actions/assembler'

import { clearAssemblyResult } from 'store/slices/assemblyResultSlice'

import { RootState } from 'store'
import { BuildEntityParams } from 'store/types/assembler'
import { ContextSearchResult } from 'store/types/searchResults'
import { ElectronicsPartsInformation } from 'store/types'
import { componentPermissions } from 'config/permissions'
import { componentNames } from 'config/roles'
import { RoutePaths } from 'routes/paths'
import { Typography } from '@mui/material'

const DEFAULT_COUNTRY_CODE = 'oecd1'

const ProductDetailPage = () => {
  const { sid } = useParams<{ sid: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const countryCodeFromUrl =
    searchParams.get('countryCode') || DEFAULT_COUNTRY_CODE

  const assemblyData = useSelector(
    (state: RootState) => state.assemblyResult.assemblyResponse?.data
  )

  const countries: ContextSearchResult[] | undefined = useSelector(
    (state: RootState) =>
      state.searchResults.searchResultsCountries?.data.results
  )

  const [selectedCountry, setSelectedCountry] =
    useState<ContextSearchResult | null>(null)

  useEffect(() => {
    dispatch(sagaGetSearchResultsAction('.energy.countries'))
  }, [dispatch])

  useEffect(() => {
    if (!sid) return

    dispatch(clearAssemblyResult())

    const params: BuildEntityParams = {
      countryCode: countryCodeFromUrl,
      recyclingRatio: 0,
    }

    dispatch(sagaGetPreMadeAssemblyAction({ sid, p: params }))
  }, [dispatch, sid, countryCodeFromUrl])

  useEffect(() => {
    if (!countries) return
    const match = countries.find(
      (c) =>
        c.fullContext?.shortName?.toLowerCase() ===
        countryCodeFromUrl.toLowerCase()
    )
    if (match) {
      setSelectedCountry(match)
    }
  }, [countries, countryCodeFromUrl])

  const handleCountryChange = (country: ContextSearchResult) => {
    setSelectedCountry(country)
    const newCountryCode =
      country?.fullContext?.shortName || DEFAULT_COUNTRY_CODE
    setSearchParams({ countryCode: newCountryCode })
  }

  if (!assemblyData) {
    return (
      <ContentBodyWrapper title={'Entity detail'}>
        <div className="flex flex-col items-center justify-center py-12">
          <SustamizeSpinner />
        </div>
      </ContentBodyWrapper>
    )
  }

  const { information, result } = assemblyData
  const electronicsInfo = information as ElectronicsPartsInformation

  return (
    <ContentBodyWrapper title={'Entity detail'}>
      <div className="container mx-auto px-4 py-8 space-y-8">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(RoutePaths.toolboxElectronicsV2)}
          className="mb-2"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>

        <div className="space-y-6">
          <Typography
            variant="h4"
            className="h-full px-4"
            fontSize="1.5rem"
            fontWeight="500"
          >
            Search parameters
          </Typography>

          <Card className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <CountrySelector
                countries={countries ?? []}
                value={selectedCountry?.sid}
                onChange={handleCountryChange}
              />
            </div>
          </Card>
        </div>

        <EmissionHeader
          identification={electronicsInfo.identification}
          general={electronicsInfo.general}
          technical={electronicsInfo.technical}
          timestamps={electronicsInfo.timestamps}
        />

        <EmissionSummary
          totalEmissions={result?.totalEmissions}
          emissionsByCategory={result?.emissionsByCategory}
        />

        <EmissionCharts
          emissionOriginComposition={result?.emissionOriginComposition}
          emissionsByCategory={result?.emissionsByCategory}
        />

        <ProcessChain processChain={result?.calculationProcessChain ?? []} />
      </div>
    </ContentBodyWrapper>
  )
}

const electronicsV2DetailsPage = {
  routeProps: {
    path: RoutePaths.toolboxElectronicsV2Details,
    title: 'Entity detail',
    element: <ProductDetailPage />,
    roles: componentPermissions[componentNames.pageElectronicsV2],
    requiresAuthentication: true,
  },
  name: 'Electronics V2',
}

export default electronicsV2DetailsPage
